// constants
import { DOCUMENTS } from './Forms/PrivacyAndConsentForm'
import { LANGUAGE_OPTIONS } from 'constants/index'
import { CURRENCIES_OPTIONS } from 'constants/currencies'
import { LICENCE_TYPES_OPTIONS, LICENCES_TYPES, SUBSCRIPTION_LEVEL_OPTIONS } from 'constants/licences'
// types
import { IOption } from 'components/FormElements/Select'
import { ICompany } from 'types/graphql'
// utils
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import { generateTimezoneOptions } from 'utils/date'
import { getDateTimeFormatOptions } from 'constants/date'
import { getOptionByValue, getValueFromOption, getValuesFromOptions, toOption } from 'utils/form'

export const prepareForAPI = (data: any, isAdmin: boolean) => {
  const rawLicences = get(data, 'licences', {})

  const licences = Object.keys(rawLicences)
    .map((key) => {
      const licence = get(rawLicences, key, {})
      const licenceType = getValueFromOption(licence.licenceType)

      const values: any = {
        name: key,
        active: licence.active,
        type: licenceType,
        subscriptionLevel: getValueFromOption(licence.subscriptionLevel)
      }

      const isUserType = LICENCES_TYPES.USER === licenceType

      values.standartCount = isUserType ? licence.standardLicences : null

      return values
    })
    .filter(({ type }) => type)

  return {
    name: data.name,
    code: data.code,
    domainName: `${data.domainName}.skefto.com`,
    language: getValueFromOption(data.language),
    dateAndNumberFormat: getValueFromOption(data.dateAndNumberFormat),
    financialYearStartsAt: data.financialYearStartsAt,
    financialYearFinishesAt: data.financialYearFinishesAt,
    sector: getValuesFromOptions(data.sector),
    timezone: getValueFromOption(data.timezone),
    businessDays: data.businessDays.map((day: IOption) => ({
      key: day.value,
      startTime: day.startTime,
      finishTime: day.finishTime
    })),
    businessNumber: data.businessNumber,
    address: data.address?.value?.address,
    companyNumber: data.companyNumber,
    legitimateInterest: get(data, `documents[${DOCUMENTS.LEGITIMATE}].description`, ''),
    privacyPolicy: get(data, `documents[${DOCUMENTS.POLICY}].description`, ''),
    consentToCommunicate: get(data, `documents[${DOCUMENTS.CONSENT}].description`, ''),
    licences,
    currency: getValueFromOption(data.currency),
    logoUrl: data.logoUrl,
    ...(isAdmin && {
      refreshTokenAbsoluteTtl: data.refreshTokenAbsoluteTtl
        ? Number(data.refreshTokenAbsoluteTtl)
        : null,
      idleTimeoutSeconds: data.idleTimeoutSeconds ? Number(data.idleTimeoutSeconds) : null,
      twoFactorEnforced: Boolean(data.twoFactorEnforced)
    })
  }
}

export const parseFromApi = (company: ICompany) => {
  let values

  const rawLicences = get(company, 'licences', [])
  const licences = {}

  if (Array.isArray(rawLicences)) {
    rawLicences.forEach((licence) => {
      licences[licence.name] = {
        active: licence.active,
        licenceType: getOptionByValue(licence.type, LICENCE_TYPES_OPTIONS),
        standardLicences: licence.standartCount,
        subscriptionLevel: getOptionByValue(licence.subscriptionLevel, SUBSCRIPTION_LEVEL_OPTIONS)
      }
    })
  }

  values = {
    name: company.name,
    code: company.code,
    domainName: company.domainName.replace('.skefto.com', ''),
    language: getOptionByValue(company.language, LANGUAGE_OPTIONS),
    dateAndNumberFormat: getOptionByValue(company.dateAndNumberFormat, getDateTimeFormatOptions()),
    financialYearStartsAt: company.financialYearStartsAt,
    financialYearFinishesAt: company.financialYearFinishesAt,
    sector: company.sector.map((item) => toOption(item)),
    timezone: getOptionByValue(company.timezone, generateTimezoneOptions()),
    businessDays: isEmpty(company.businessDays)
      ? []
      : company.businessDays.map((day) => ({
          label: day.key,
          value: day.key,
          startTime: day.startTime,
          finishTime: day.finishTime
        })),
    businessNumber: company.businessNumber,
    companyNumber: company.companyNumber,
    address: company.address,
    currency: getOptionByValue(company.currency, CURRENCIES_OPTIONS),
    logoUrl: company.logoUrl,
    refreshTokenAbsoluteTtl: company.refreshTokenAbsoluteTtl,
    idleTimeoutSeconds: company.idleTimeoutSeconds,
    twoFactorEnforced: company.twoFactorEnforced ?? false
  }

  values = {
    ...values,
    licences,
    documents: {
      [DOCUMENTS.LEGITIMATE]: {
        description: company.legitimateInterest
      },
      [DOCUMENTS.POLICY]: {
        description: company.privacyPolicy
      },
      [DOCUMENTS.CONSENT]: {
        description: company.consentToCommunicate
      }
    }
  }

  return values
}
